import React, { useContext, useEffect, useState, useCallback } from "react";
import { CanvasContext } from "../../../contexts/CanvasContext";
import AnimationPopup from "../RenderCompnent/AnimationPopup";
import { PropertyContext } from "../../../contexts/PropertyContext";

const ImageProperties = ({ selectedImage, handleChange, searchTerm }) => {
  const { pages, files, setShowFileManager } = useContext(CanvasContext);
  const [selectedImageFile, setSelectedImageFile] = useState("");
  const [isAnimationPopupOpen, setIsAnimationPopupOpen] = useState(false);
  const [selectedJsonFile, setSelectedJsonFile] = useState(null);
  const [jsonIds, setJsonIds] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [jsonObjects, setJsonObjects] = useState([]);

  const {
    renderMarginProperties,
    renderPaddingProperties,
    renderBorderProperties,
    renderPositionProperties,
    renderHoveredMarginProperties,
    renderFocusedMarginProperties,
    renderActivatedMarginProperties,
    renderHoveredPaddingProperties,
    renderFocusedPaddingProperties,
    renderActivatedPaddingProperties,
    renderHoveredBorderProperties,
    renderFocusedBorderProperties,
    renderActivatedBorderProperties,
    renderHoveredPositionProperties,
    renderFocusedPositionProperties,
    renderActivatedPositionProperties,
  } = useContext(PropertyContext);

  useEffect(() => {
    if (
      selectedImage &&
      selectedImage.src &&
      selectedImage.src.startsWith("./")
    ) {
      setSelectedImageFile(selectedImage.src.replace("./", ""));
    } else {
      setSelectedImageFile("");
    }
  }, [selectedImage]);

  useEffect(() => {
    if (selectedJsonFile) {
      try {
        const jsonData = JSON.parse(selectedJsonFile.data);
        const ids = jsonData.map((item) => item._id.toString());
        setJsonIds(ids);
      } catch (error) {
        console.error("Error parsing JSON file:", error);
      }
    }
  }, [selectedJsonFile]);

  const findImagePaths = useCallback((obj, path = "") => {
    const result = [];
    for (const [key, value] of Object.entries(obj)) {
      const newPath = path ? `${path}.${key}` : key;
      if (typeof value === "string" && value.trim() !== "") {
        result.push([newPath, value]);
      } else if (typeof value === "object" && value !== null) {
        result.push(...findImagePaths(value, newPath));
      }
    }
    return result;
  }, []);

  useEffect(() => {
    if (selectedJsonFile && selectedId) {
      try {
        const jsonData = JSON.parse(selectedJsonFile.data);
        const selectedItem = jsonData.find(
          (item) => item._id.toString() === selectedId
        );
        if (selectedItem) {
          const imagePaths = findImagePaths(selectedItem);
          setJsonObjects(imagePaths);
        } else {
          setJsonObjects([]);
        }
      } catch (error) {
        console.error("Error parsing JSON data:", error);
      }
    }
  }, [selectedJsonFile, selectedId, findImagePaths]);

  const renderAnimationButton = () => {
    if (searchTerm && !"animation".includes(searchTerm.toLowerCase())) {
      return null;
    }
    return (
      <div style={styles.inputGroup}>
        <button
          onClick={() => setIsAnimationPopupOpen(true)}
          style={styles.animationButton}
        >
          Open Animation Editor
        </button>
      </div>
    );
  };

  const renderInput = (label, name, type = "text", additionalProps = {}) => {
    if (searchTerm && !label.toLowerCase().includes(searchTerm.toLowerCase())) {
      return null;
    }
    return (
      <div style={styles.inputGroup}>
        <label style={styles.label}>{label}: </label>
        <input
          type={type}
          name={name}
          value={selectedImage ? selectedImage[name] : ""}
          onChange={handleChange}
          style={styles.input}
          {...additionalProps}
        />
      </div>
    );
  };

  const renderSelect = (label, name, options) => {
    if (searchTerm && !label.toLowerCase().includes(searchTerm.toLowerCase())) {
      return null;
    }
    return (
      <div key={name} style={styles.inputGroup}>
        <label style={styles.label}>{label}: </label>
        <select
          name={name}
          value={selectedImage ? selectedImage[name] : ""}
          onChange={handleChange}
          style={styles.select}
        >
          <option value="">Select</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderRedirectSelect = () => {
    if (searchTerm && !"redirect to".includes(searchTerm.toLowerCase())) {
      return null;
    }
    return (
      <div style={styles.inputGroup}>
        <label style={styles.label}>Redirect To: </label>
        <select
          name="redirectTo"
          value={selectedImage ? selectedImage.redirectTo || "" : ""}
          onChange={handleChange}
          style={styles.select}
        >
          <option value="">No Redirect</option>
          {pages.slice(1).map((page) => (
            <option key={page.id} value={page.name}>
              {page.name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderMutableInput = (label, name, array) => {
    if (searchTerm && !label.toLowerCase().includes(searchTerm.toLowerCase())) {
      return null;
    }
    return (
      <div key={name} style={styles.inputGroup}>
        <label style={styles.label}>{label}: </label>
        <div style={styles.widthHeightInputGroup}>
          <select
            name={name}
            value={selectedImage ? selectedImage[name] : ""}
            onChange={handleChange}
            style={styles.select}
          >
            {array.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <input
            type="text"
            name={name}
            value={selectedImage ? selectedImage[name] : ""}
            onChange={handleChange}
            style={styles.input}
          />
        </div>
      </div>
    );
  };

  const handleImageSelect = (imageName) => {
    setSelectedImageFile(imageName);
    handleChange({
      target: { name: "src", value: `./${imageName}` },
    });
  };

  const renderJsonFileSelect = () => {
    const jsonFiles = files.filter((file) => file.type === "application/json");
    return (
      <div style={styles.inputGroup}>
        <label style={styles.label}>Select JSON File: </label>
        <select
          style={styles.select}
          onChange={(e) => {
            const file = jsonFiles.find(
              (f) => f.id === parseInt(e.target.value)
            );
            setSelectedJsonFile(file);
            setSelectedId("");
          }}
        >
          <option value="">Select a JSON file</option>
          {jsonFiles.map((file) => (
            <option key={file.id} value={file.id}>
              {file.name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderJsonIdSelect = () => {
    return (
      <div style={styles.inputGroup}>
        <label style={styles.label}>Select ID: </label>
        <select
          style={styles.select}
          value={selectedId}
          onChange={(e) => {
            setSelectedId(e.target.value);
          }}
        >
          <option value="">Select an ID</option>
          {jsonIds.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderJsonObjectSelect = () => {
    return (
      <div style={styles.inputGroup}>
        <label style={styles.label}>Select Image Source: </label>
        <select
          style={styles.select}
          onChange={(e) => {
            const [, value] = jsonObjects[parseInt(e.target.value)];
            handleChange({
              target: { name: "src", value: value },
            });
          }}
        >
          <option value="">Select an image source</option>
          {jsonObjects.map(([key, value], index) => (
            <option key={index} value={index}>
              {`${key}: ${value}`}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {renderJsonFileSelect()}
      {selectedJsonFile && renderJsonIdSelect()}
      {selectedId && renderJsonObjectSelect()}
      <div key="image-source" style={styles.inputGroup}>
        <label style={styles.label}>Image Source: </label>
        <input
          type="text"
          name="src"
          value={selectedImage ? selectedImage.src : ""}
          onChange={handleChange}
          placeholder="Enter URL or select from File Manager"
          style={styles.input}
        />
        <button onClick={() => setShowFileManager(true)} style={styles.button}>
          Open File Manager
        </button>
      </div>
      {files.length > 0 && (
        <div key="image-selector" style={styles.imageSelector}>
          <label style={styles.label}>Select an image:</label>
          <select
            onChange={(e) => handleImageSelect(e.target.value)}
            value={selectedImageFile || ""}
            style={styles.select}
          >
            <option value="">Choose an image</option>
            {files
              .filter((file) => file.type.startsWith("image/"))
              .map((file) => (
                <option key={file.id} value={file.name}>
                  {file.name}
                </option>
              ))}
          </select>
        </div>
      )}
      {renderInput("Alt Text", "alt")}
      {renderInput("Title", "title")}
      {renderRedirectSelect()}
      {renderInput("Redirect URL", "redirectUrl")}

      {renderMutableInput("Width", "width", [
        "auto",
        "fit-content",
        "max-content",
        "min-content",
      ])}
      {renderMutableInput("Height", "height", [
        "auto",
        "fit-content",
        "max-content",
        "min-content",
      ])}
      {renderMarginProperties(renderMutableInput)}
      {renderPaddingProperties(renderMutableInput)}
      {renderBorderProperties(renderMutableInput, renderInput, renderSelect)}
      {renderPositionProperties(renderMutableInput, renderInput, renderSelect)}

      {renderMutableInput("Box Shadow", "boxShadow", [
        "none",
        "2px 4px 10px #000000",
      ])}
      {renderInput("Opacity", "opacity", "number", {
        min: 0,
        max: 1,
        step: 0.01,
      })}
      {renderSelect("Box Sizing", "boxSizing", ["content-box", "border-box"])}
      {renderSelect("Display", "display", ["inline", "inline-block", "block"])}
      {renderSelect("Visibility", "visibility", [
        "visible",
        "hidden",
        "collapse",
        "inherit",
        "initial",
        "unset",
      ])}
      {renderSelect("Object Fit", "objectFit", [
        "fill",
        "contain",
        "cover",
        "none",
        "scale-down",
      ])}
      {renderSelect("Object Position", "objectPosition", [
        "top",
        "right",
        "bottom",
        "left",
        "center",
      ])}
      {renderMutableInput("Filter", "filter", [
        "none",
        "blur(5px)",
        "brightness(0.5)",
        "contrast(150%)",
        "drop-shadow(10px 10px 5px grey)",
        "grayscale(100%)",
        "hue-rotate(90deg)",
        "invert(100%)",
        "opacity(50%)",
        "saturate(200%)",
        "sepia(100%)",
      ])}
      {renderSelect("Backdrop Filter Active", "activatedbackdropFilter", [
        "none",
        "blur(5px)",
        "brightness(0.5)",
        "contrast(150%)",
        "drop-shadow(10px 10px 5px grey)",
        "grayscale(100%)",
        "hue-rotate(90deg)",
        "invert(100%)",
        "opacity(50%)",
        "saturate(200%)",
        "sepia(100%)",
      ])}
      {renderSelect("Mix Blend Mode", "mixBlendMode", [
        "normal",
        "multiply",
        "screen",
        "overlay",
        "darken",
        "lighten",
        "color-dodge",
        "color-burn",
        "hard-light",
        "soft-light",
        "difference",
        "exclusion",
        "hue",
        "saturation",
        "color",
        "luminosity",
      ])}

      {renderSelect("Cursor Type", "cursor", [
        "default",
        "pointer",
        "text",
        "move",
        "wait",
        "help",
        "not-allowed",
        "crosshair",
        "cell",
        "vertical-text",
        "alias",
        "copy",
        "no-drop",
        "grab",
        "grabbing",
        "e-resize",
        "n-resize",
        "ne-resize",
        "nw-resize",
        "s-resize",
        "se-resize",
        "sw-resize",
        "w-resize",
        "ns-resize",
        "ew-resize",
        "nesw-resize",
        "nwse-resize",
        "zoom-in",
        "zoom-out",
        "all-scroll",
      ])}
      {renderMutableInput("Transform Active", "activatedtransform", [
        "none",
        "translate(100px, 200px)",
        "translateX(100px)",
        "translateY(200px)",
        "scale(1.5, 1.6)",
        "scaleX(1.5)",
        "scaleY(1.6)",
        "rotate(30deg)",
        "skew(30deg, 10deg)",
        "skewX(30deg)",
        "skewY(10deg)",
        "matrix(1, 0.5, -0.5, 1, 100, 200)",
      ])}
      {renderMutableInput(
        "Transition Timing Function Active",
        "activatedtransition",
        [
          "none",
          "ease 3s",
          "linear 3s",
          "ease-in 3s",
          "ease-out 3s",
          "ease-in-out 3s",
          "cubic-bezier(0.25, 0.1, 0.25, 1.0)",
        ]
      )}
      {renderAnimationButton()}
      <AnimationPopup
        isOpen={isAnimationPopupOpen}
        onClose={() => setIsAnimationPopupOpen(false)}
        selectedDiv={selectedImage}
        handleChange={handleChange}
      />
      {renderSelect("Pseudo Classes", "pseudoClass", [
        "none",
        "hover",
        "focus",
        "active",
      ])}
      {selectedImage && selectedImage.pseudoClass === "hover" && (
        <>
          {/* {renderMutableInput("Width Hover", "hoveredwidth", [
            "auto",
            "fit-content",
            "max-content",
            "min-content",
            "100%",
            "100vw",
          ])}
          {renderMutableInput("Height Hover", "hoveredheight", [
            "auto",
            "fit-content",
            "max-content",
            "min-content",
            "100%",
            "100vh",
          ])} */}
          {renderHoveredMarginProperties(renderMutableInput)}
          {renderHoveredPaddingProperties(renderMutableInput)}
          {renderHoveredBorderProperties(
            renderMutableInput,
            renderInput,
            renderSelect
          )}
          {renderHoveredPositionProperties(
            renderMutableInput,
            renderInput,
            renderSelect
          )}
          {renderMutableInput("Box Shadow Hover", "hoveredboxShadow", [
            "none",
            "2px 4px 10px #000000",
          ])}
          {renderInput("Opacity Hover", "hoveredOpacity", "number", {
            min: 0,
            max: 1,
            step: 0.01,
          })}
          {renderSelect("Cursor Hover", "hoveredcursor", [
            "default",
            "pointer",
            "text",
            "move",
            "wait",
            "help",
            "not-allowed",
            "crosshair",
            "cell",
            "vertical-text",
            "alias",
            "copy",
            "no-drop",
            "grab",
            "grabbing",
            "e-resize",
            "n-resize",
            "ne-resize",
            "nw-resize",
            "s-resize",
            "se-resize",
            "sw-resize",
            "w-resize",
            "ns-resize",
            "ew-resize",
            "nesw-resize",
            "nwse-resize",
            "zoom-in",
            "zoom-out",
            "all-scroll",
          ])}
          {renderSelect("Visibility Hover", "hoveredvisibility", [
            "visible",
            "hidden",
            "collapse",
            "inherit",
            "initial",
            "unset",
          ])}
          {renderSelect("Filter Hover", "hoveredfilter", [
            "none",
            "blur(5px)",
            "brightness(0.5)",
            "contrast(150%)",
            "drop-shadow(10px 10px 5px grey)",
            "grayscale(100%)",
            "hue-rotate(90deg)",
            "invert(100%)",
            "opacity(50%)",
            "saturate(200%)",
            "sepia(100%)",
          ])}
          {renderSelect("Backdrop Filter Hover", "hoveredbackdropFilter", [
            "none",
            "blur(5px)",
            "brightness(0.5)",
            "contrast(150%)",
            "drop-shadow(10px 10px 5px grey)",
            "grayscale(100%)",
            "hue-rotate(90deg)",
            "invert(100%)",
            "opacity(50%)",
            "saturate(200%)",
            "sepia(100%)",
          ])}
          {renderSelect("Mix Blend Mode Hover", "hoveredmixBlendMode", [
            "normal",
            "multiply",
            "screen",
            "overlay",
            "darken",
            "lighten",
            "color-dodge",
            "color-burn",
            "hard-light",
            "soft-light",
            "difference",
            "exclusion",
            "hue",
            "saturation",
            "color",
            "luminosity",
          ])}

          {renderMutableInput("Transform Hover", "hoveredtransform", [
            "none",
            "translate(100px, 200px)",
            "translateX(100px)",
            "translateY(200px)",
            "scale(1.5, 1.6)",
            "scaleX(1.5)",
            "scaleY(1.6)",
            "rotate(30deg)",
            "skew(30deg, 10deg)",
            "skewX(30deg)",
            "skewY(10deg)",
            "matrix(1, 0.5, -0.5, 1, 100, 200)",
          ])}
          {renderMutableInput(
            "Transition Timing Function",
            "hoveredtransition",
            [
              "none",
              "ease 3s",
              "linear 3s",
              "ease-in 3s",
              "ease-out 3s",
              "ease-in-out 3s",
              "cubic-bezier(0.25, 0.1, 0.25, 1.0)",
            ]
          )}
        </>
      )}
      {selectedImage && selectedImage.pseudoClass === "focus" && (
        <>
          {/* {renderMutableInput("Width Focus", "focusedwidth", [
            "auto",
            "fit-content",
            "max-content",
            "min-content",
            "100%",
            "100vw",
          ])}
          {renderMutableInput("Height Focus", "focusedheight", [
            "auto",
            "fit-content",
            "max-content",
            "min-content",
            "100%",
            "100vh",
          ])} */}
          {renderFocusedMarginProperties(renderMutableInput)}
          {renderFocusedPaddingProperties(renderMutableInput)}
          {renderFocusedBorderProperties(
            renderMutableInput,
            renderInput,
            renderSelect
          )}
          {renderFocusedPositionProperties(
            renderMutableInput,
            renderInput,
            renderSelect
          )}
          {renderMutableInput("Box Shadow Focus", "focusedboxShadow", [
            "none",
            "2px 4px 10px #000000",
          ])}
          {renderInput("Opacity Focus", "focusedOpacity", "number", {
            min: 0,
            max: 1,
            step: 0.01,
          })}
          {renderSelect("Cursor Focus", "focusedcursor", [
            "default",
            "pointer",
            "text",
            "move",
            "wait",
            "help",
            "not-allowed",
            "crosshair",
            "cell",
            "vertical-text",
            "alias",
            "copy",
            "no-drop",
            "grab",
            "grabbing",
            "e-resize",
            "n-resize",
            "ne-resize",
            "nw-resize",
            "s-resize",
            "se-resize",
            "sw-resize",
            "w-resize",
            "ns-resize",
            "ew-resize",
            "nesw-resize",
            "nwse-resize",
            "zoom-in",
            "zoom-out",
            "all-scroll",
          ])}
          {renderSelect("Visibility Focus", "focusedvisibility", [
            "visible",
            "hidden",
            "collapse",
            "inherit",
            "initial",
            "unset",
          ])}
          {renderSelect("Filter Focus", "focusedfilter", [
            "none",
            "blur(5px)",
            "brightness(0.5)",
            "contrast(150%)",
            "drop-shadow(10px 10px 5px grey)",
            "grayscale(100%)",
            "hue-rotate(90deg)",
            "invert(100%)",
            "opacity(50%)",
            "saturate(200%)",
            "sepia(100%)",
          ])}
          {renderSelect("Backdrop Filter Focus", "focusedbackdropFilter", [
            "none",
            "blur(5px)",
            "brightness(0.5)",
            "contrast(150%)",
            "drop-shadow(10px 10px 5px grey)",
            "grayscale(100%)",
            "hue-rotate(90deg)",
            "invert(100%)",
            "opacity(50%)",
            "saturate(200%)",
            "sepia(100%)",
          ])}
          {renderSelect("Mix Blend Mode Focus", "focusedmixBlendMode", [
            "normal",
            "multiply",
            "screen",
            "overlay",
            "darken",
            "lighten",
            "color-dodge",
            "color-burn",
            "hard-light",
            "soft-light",
            "difference",
            "exclusion",
            "hue",
            "saturation",
            "color",
            "luminosity",
          ])}

          {renderMutableInput("Transform Focus", "focusedtransform", [
            "none",
            "translate(100px, 200px)",
            "translateX(100px)",
            "translateY(200px)",
            "scale(1.5, 1.6)",
            "scaleX(1.5)",
            "scaleY(1.6)",
            "rotate(30deg)",
            "skew(30deg, 10deg)",
            "skewX(30deg)",
            "skewY(10deg)",
            "matrix(1, 0.5, -0.5, 1, 100, 200)",
          ])}
          {renderMutableInput(
            "Transition Timing Function Focus",
            "focusedtransition",
            [
              "none",
              "ease 3s",
              "linear 3s",
              "ease-in 3s",
              "ease-out 3s",
              "ease-in-out 3s",
              "cubic-bezier(0.25, 0.1, 0.25, 1.0)",
            ]
          )}
        </>
      )}
      {selectedImage && selectedImage.pseudoClass === "active" && (
        <>
          {/* {renderMutableInput("Width Active", "activatedwidth", [
            "auto",
            "fit-content",
            "max-content",
            "min-content",
            "100%",
            "100vw",
          ])}
          {renderMutableInput("Height Active", "activatedheight", [
            "auto",
            "fit-content",
            "max-content",
            "min-content",
            "100%",
            "100vh",
          ])} */}
          {renderActivatedMarginProperties(renderMutableInput)}
          {renderActivatedPaddingProperties(renderMutableInput)}
          {renderActivatedBorderProperties(
            renderMutableInput,
            renderInput,
            renderSelect
          )}
          {renderActivatedPositionProperties(
            renderMutableInput,
            renderInput,
            renderSelect
          )}
          {renderMutableInput("Box Shadow Active", "activatedboxShadow", [
            "none",
            "2px 4px 10px #000000",
          ])}
          {renderInput("Opacity Active", "activatedOpacity", "number", {
            min: 0,
            max: 1,
            step: 0.01,
          })}
          {renderSelect("Cursor Active", "activatedcursor", [
            "default",
            "pointer",
            "text",
            "move",
            "wait",
            "help",
            "not-allowed",
            "crosshair",
            "cell",
            "vertical-text",
            "alias",
            "copy",
            "no-drop",
            "grab",
            "grabbing",
            "e-resize",
            "n-resize",
            "ne-resize",
            "nw-resize",
            "s-resize",
            "se-resize",
            "sw-resize",
            "w-resize",
            "ns-resize",
            "ew-resize",
            "nesw-resize",
            "nwse-resize",
            "zoom-in",
            "zoom-out",
            "all-scroll",
          ])}
          {renderSelect("Visibility Active", "activatedvisibility", [
            "visible",
            "hidden",
            "collapse",
            "inherit",
            "initial",
            "unset",
          ])}
          {renderSelect("Filter Active", "activatedfilter", [
            "none",
            "blur(5px)",
            "brightness(0.5)",
            "contrast(150%)",
            "drop-shadow(10px 10px 5px grey)",
            "grayscale(100%)",
            "hue-rotate(90deg)",
            "invert(100%)",
            "opacity(50%)",
            "saturate(200%)",
            "sepia(100%)",
          ])}
          {renderSelect("Backdrop Filter Active", "activatedbackdropFilter", [
            "none",
            "blur(5px)",
            "brightness(0.5)",
            "contrast(150%)",
            "drop-shadow(10px 10px 5px grey)",
            "grayscale(100%)",
            "hue-rotate(90deg)",
            "invert(100%)",
            "opacity(50%)",
            "saturate(200%)",
            "sepia(100%)",
          ])}
          {renderSelect("Mix Blend Mode Active", "activatedmixBlendMode", [
            "normal",
            "multiply",
            "screen",
            "overlay",
            "darken",
            "lighten",
            "color-dodge",
            "color-burn",
            "hard-light",
            "soft-light",
            "difference",
            "exclusion",
            "hue",
            "saturation",
            "color",
            "luminosity",
          ])}
          {renderMutableInput("Transform Active", "activatedtransform", [
            "none",
            "translate(100px, 200px)",
            "translateX(100px)",
            "translateY(200px)",
            "scale(1.5, 1.6)",
            "scaleX(1.5)",
            "scaleY(1.6)",
            "rotate(30deg)",
            "skew(30deg, 10deg)",
            "skewX(30deg)",
            "skewY(10deg)",
            "matrix(1, 0.5, -0.5, 1, 100, 200)",
          ])}
          {renderMutableInput(
            "Transition Timing Function Active",
            "activatedtransition",
            [
              "none",
              "ease 3s",
              "linear 3s",
              "ease-in 3s",
              "ease-out 3s",
              "ease-in-out 3s",
              "cubic-bezier(0.25, 0.1, 0.25, 1.0)",
            ]
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: "5px",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    overflow: "hidden",
  },
  heading: {
    fontSize: "15px",
    margin: "10px 0",
    color: "#333",
    borderBottom: "1px solid #ccc",
    paddingBottom: "5px",
  },
  inputGroup: {
    margin: "10px 0",
  },
  label: {
    display: "block",
    marginBottom: "5px",
    fontWeight: "bold",
  },
  input: {
    width: "100%",
    padding: "3px 5px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "8px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  filterInputGroup: {
    display: "flex",
    gap: "10px",
  },
  button: {
    marginTop: "5px",
    padding: "8px",
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  imageSelector: {
    marginTop: "10px",
  },
  animationButton: {
    width: "100%",
    padding: "10px",
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
};

export default ImageProperties;
